import React, { ReactNode, useRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import styled from '@emotion/styled';

import { InputLabel } from '../InputLabel/component';
import { AutoSaveState } from '../AutoSaveIndicator/component';
import { INPUT_DESCRIPTION, INPUT_BOX, INPUT_BUTTON } from '../Style/input';

export interface FileFieldProps {
  id?: string;
  name?: string;
  label?: ReactNode;
  className?: string;
  value?: string;
  placeholder?: string;
  onChange?: ((event: React.ChangeEvent<HTMLInputElement>) => void) | null;
  onValueChange?: ((value: File | null) => void) | null;
  description?: ReactNode;
  error?: string | null;
  required?: boolean;
  focusOnMount?: boolean;
  autoSave?: boolean;
  autoSaveState?: AutoSaveState;
  // accept only specific file types, must be comma seperated string (".xls,.xlsx")
  accept?: string;
}

const FileFieldComponent = styled('div')({
  display: 'block',
});

const SelectedFileAndButton = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
});

const Description = styled('div')(INPUT_DESCRIPTION);

const SelectedFileField = styled('div')({
  ...INPUT_BOX,
  width: '100%',
  marginBottom: 0,
  minWidth: 50,
  flexBasis: 50,
  flexGrow: 1,
  flexShrink: 1,
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
});

const Button = styled('button')({ ...INPUT_BUTTON, boxSizing: 'border-box' });

const HiddenFileInput = styled('input')({
  display: 'none',
});

export const FileField = React.memo(
  ({
    id,
    name,
    label,
    className,
    value,
    placeholder = 'No file selected',
    onChange = null,
    onValueChange = null,
    description,
    error,
    required,
    focusOnMount,
    autoSave,
    autoSaveState,
    accept,
  }: FileFieldProps) => {
    const inputId: string = uniqueId();
    const fileInputEl = useRef<HTMLInputElement | null>(null);
    const buttonEl = useRef<HTMLButtonElement | null>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
      if (focusOnMount && buttonEl.current) {
        buttonEl.current.focus();
      }
    }, []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { files } = event.target;
      const file = files && files.length ? files[0] : null;

      if (file) {
        setSelectedFile(file);

        if (onChange) {
          onChange(event);
        }

        if (onValueChange) {
          onValueChange(file);
        }
      }
    };

    const handleSelectClick = (): void => {
      if (fileInputEl.current) {
        fileInputEl.current.click();
      }
    };

    return (
      <FileFieldComponent className={classNames(className)}>
        {label && (
          <InputLabel
            htmlFor={id || inputId}
            error={error}
            required={required}
            autoSave={autoSave}
            autoSaveState={autoSaveState}
          >
            {label}
          </InputLabel>
        )}

        {description && <Description>{description}</Description>}
        <HiddenFileInput
          {...{ name }}
          id={id || inputId}
          onChange={handleChange}
          type="file"
          value={value}
          ref={fileInputEl}
          accept={accept || ''}
        />
        <SelectedFileAndButton>
          <SelectedFileField>
            {selectedFile ? selectedFile.name : placeholder}
          </SelectedFileField>
          <Button
            type="button"
            onClick={handleSelectClick}
            className="ol-file-field-button"
            ref={buttonEl}
          >
            Select file
          </Button>
        </SelectedFileAndButton>
      </FileFieldComponent>
    );
  }
);
